import React, { useState, useEffect } from 'react'

import Intro    from '../Intro'
import Backdrop from '../fiat/Backdrop'
import useRecord from '../fiat/useRecord'

import useBeneficiarySelect from '../useBeneficiarySelect'

// Wraps Stock and DAF in a shared two-stage form. The donor confirms
// their nonprofit / project / plugin-data selection on the first stage;
// the third-party SDK (DonateStock EasyButton or Chariot Connect) mounts
// inside a backdrop on the second.
//
// Draft system: in twoStep mode, saveDraft fires on Continue and the
// resulting orderToken / orderUuid are threaded into the Button. In
// single-step mode the Button JIT-saveDrafts itself inside its own
// post-success handler via the `record` it receives. Same backend
// path either way, no draft created until the donor commits — in
// single-step the commit lands at the third-party SDK; in twoStep
// it lands at our Continue button.
const Launcher = ({
  Button, plugins, paymentMethod, paymentPartnerName, i18n, commonInputProps,
  partner_item, partner_items, goal_partner_item, footer,
  order, partner, session, donationsPath, donationTags, embeddedOn,
  token, template, pledge_id, pdt,
  ...props
}) => {
  const [ show, setShow ] = useState(false)
  const [ loading, setLoading ] = useState(false)
  const [ draftError, setDraftError ] = useState(null)
  const [ orderToken, setOrderToken ] = useState(null)
  const [ orderUuid, setOrderUuid ] = useState(null)

  const { partnerItem, projectId, ...beneficiarySelect } = useBeneficiarySelect({
    i18n, commonInputProps, partner_item, partner_items, goal_partner_item,
    disable_donation_split: true,
  })

  // Synthetic donation shape for the shared fiat useRecord. Stock and
  // DAF collect no donor input at the draft stage (the third-party SDK
  // gathers name/email/amount), so all fields are blank/null.
  //
  // amount is null because we genuinely don't know it at this stage.
  // The server overrides whatever we post here with
  // wallet_aware_min_amount via donations_controller#stock_and_daf_params
  // — that placeholder lets set_up_order_items build an OrderItem
  // (which the project_id carry-over needs) without failing the
  // min_amount check. The real amount lands later from the SDK's
  // onSuccess payload (DAF) or the DonateStock webhook (Stock).
  //
  // principalOnly: true on saveDraft short-circuits the only paths in
  // useRecord that would otherwise read donation.total / donation.tip.
  const launcherDonation = {
    state: { amount: null, firstName: '', lastName: '', email: '', phone: '' },
    shares: [],
    // Note this stub has no `iso_code`. Nothing reads it today (stock/DAF
    // completion details come from the order serializer, or are just
    // `{ partnerItem }`), but if Launcher is ever extended to report an amount
    // to the parent page, completedDonation.js's currencyCode would find no code
    // and classify the donation as `other`. Add `iso_code` before doing that.
    currency: { id: 'usd' },
    total: '0',
    tip: 0,
  }
  // Tribute isn't wired into v6's Launcher (no UI for it). Stub
  // draftOrderProps so useRecord's spread doesn't NPE.
  const tributeStub = { draftOrderProps: () => ({}) }

  const record = useRecord({
    i18n, order, partner, partnerItem, projectId, session, donationTags,
    embeddedOn, donation: launcherDonation, plugins, tribute: tributeStub,
    donationsPath, pledge_id, pdt,
    widget: { token, template },
  })

  // Close any open Backdrop when the donor's first-stage selection
  // changes — the inner Button would otherwise stay wired to the
  // previous selection. The server-side draft is updated by the next
  // Continue (saveDraft reuses the same Order via the order_id useRecord
  // threads into params), so we don't need to touch orderToken/orderUuid
  // here.
  useEffect(() => {
    setShow(false)
  }, [ partnerItem.external_id, projectId ])

  const validateAll = () => {
    const results = [
      beneficiarySelect.validate(),
      plugins.validate(),
    ]
    return results.length == results.filter(Boolean).length
  }

  const handleSubmit = async e => {
    e.preventDefault()
    if (loading) return
    if (!validateAll()) return

    setLoading(true)
    setDraftError(null)
    try {
      // Always saveDraft on Continue — useRecord's references state
      // holds the existing order_id (if any), so the server-side
      // set_order_from_token resolves it and update-in-place, not
      // duplicate. No cache check on the client.
      const { errors, token: draftToken, serializedDonation } = await record.saveDraft({
        principalOnly: true,
        // allow_blank_name is server-controlled (donations_controller's
        // calculated_order_params sets it based on payment_source) — we
        // can't override it from here, and don't need to.
        order: { payment_source: paymentMethod },
      })

      if (errors) {
        setDraftError(errors.base?.[0] || i18n.t('errors.donation_error'))
        return
      }

      setOrderToken(draftToken)
      setOrderUuid(serializedDonation?.uuid)
      setShow(true)
    } finally {
      setLoading(false)
    }
  }

  const dismissSummary = () => setShow(false)

  const twoStep = partner_items.length > 1
    || !!goal_partner_item.projects.length
    || plugins.collectData.has(paymentMethod)

  const buttonProps = {
    ...props, partner, partnerItem, projectId, i18n, plugins,
    // In twoStep mode these arrive from a successful saveDraft and the
    // Button POSTs straight to the payment_processors endpoint. In
    // single-step mode they're null and the Button JIT-saveDrafts via
    // `record` inside its own onSuccess handler before that same POST.
    orderToken, orderUuid, record, paymentMethod,
  }

  return (
    <form onSubmit={handleSubmit}>
      <Intro i18n={i18n} />

      {plugins.renderError()}
      {plugins.renderHook('afterIntro', paymentMethod)}

      <h2 className="h5 section-heading section-heading-donation">
        {i18n.t('donation')}
      </h2>

      {beneficiarySelect.render()}
      {plugins.renderHook('afterAmounts', paymentMethod)}
      {plugins.renderHook('afterSubscription', paymentMethod)}

      {draftError && (
        <div className="alert alert-danger">{draftError}</div>
      )}

      {
        twoStep
      ? <div>
          <div className="d-grid">
            <button className="btn btn-primary" disabled={loading}>
              {i18n.t('continue')}
            </button>
          </div>

          <Backdrop show={show} position="bottom">
            <div className="bg-white p-3 p-sm-4 rounded-4 shadow-lg">
              <div className="d-flex align-items-start justify-content-between column-gap-3">
                <h2 className="h5 modal-heading modal-heading-donation-to">
                  {i18n.t('subscription_labels.one_time.lineItemTo',
                    { org: partnerItem.beneficiary.name })}
                </h2>

                <div className="mb-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={dismissSummary}
                  >
                    {i18n.t('cancel')}
                  </button>
                </div>
              </div>

              <p>
                {i18n.t('paypal_summary', { method: paymentPartnerName })}
              </p>

              <Button {...buttonProps} />
            </div>
          </Backdrop>
        </div>
      : <Button {...buttonProps} />
      }

      <div className="thankyou bg-light p-2 rounded-4 mt-3">
        <div className="thankyou-inner">
          <div className="text-center thankyou-message mb-n3">
            <p>{footer}</p>
          </div>
        </div>
      </div>
    </form>
  )
}

export default Launcher
